import React, {useEffect, useRef, useState} from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Avatar,
    Menu,
    MenuItem,
    Box,
    Grid, useTheme, Drawer, IconButton
} from "@mui/material";
import { useSessionData } from "./auth/SessionDataProvider";
import { Link } from "react-router-dom";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import VolumeControl from "./modules/elements/VolumeControl";
import UserMenu from "./modules/elements/UserMenu";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import LiveTvIcon from '@mui/icons-material/LiveTv';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GameModeSelectorCompact from "./modules/elements/GameModeSelectorCompact";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from "@mui/icons-material/Close";
import ScratchSizeControl from "./modules/elements/ScratchSizeControl";

const menuButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    color: '#b4a0eb',
    textTransform: 'uppercase',
    fontFamily: 'Orbitron, sans-serif',
    px: 2,
    py: 1.5,
    '&:hover': {
        color: '#fff',
        backgroundColor: 'rgba(180,160,235,0.2)',
    }
};

export default function MenuBar() {
    const { data, sessionData, isSmallScreen, isOnPath } = useSessionData();
    const [collapsed, setCollapsed] = useState(false);
    const lastScrollY = useRef(window.scrollY);
    const collapsedRef = useRef(false); // store the current collapsed state without triggering re-renders
    const [minimized, setMinimized] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        setMinimized(!(data.skrach?.currentQuestionId === undefined));
    }, [data.skrach?.currentQuestionId]);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const scrollingDown = currentScrollY > lastScrollY.current;

            if (scrollingDown && !collapsedRef.current) {
                collapsedRef.current = true;
                setCollapsed(true); // collapse on scroll down
            } else if (!scrollingDown && collapsedRef.current && currentScrollY < 50) {
                collapsedRef.current = false;
                setCollapsed(false); // expand on scroll up
            }
            lastScrollY.current = currentScrollY;
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);


    if (isSmallScreen && minimized) {
        return (
            <>
                <Box
                    sx={{
                        position: 'fixed',
                        top: -2,
                        left: -2,
                        zIndex: theme.zIndex.appBar + 2,
                        width: 25,
                        height: 25,
                        borderRadius: '0 0 50% 0',
                        backgroundColor: 'rgba(29,29,29,0.7)',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }}
                    onClick={() => setMinimized(false)}
                >
                    <img src="/logo64.webp" alt="logo" width="35" height="35" className="img-no-style" />
                </Box>
            </>
        );
    }

    return (
        <AppBar
            position="sticky"
            sx={{
                top: 0,
                zIndex: (theme) => theme.zIndex.drawer + 1,
                background: 'repeating-linear-gradient(45deg, rgba(122, 82, 224, 0.04), rgba(122, 82, 224, 0.07) 0px, transparent 0px, transparent 16px), radial-gradient(circle at center, rgba(28, 31, 42, 0.99), #0d0f18)',
                borderBottom: '2px solid rgba(180, 160, 235, 0.9)',
                boxShadow: '0 0 25px rgba(122, 82, 224, 0.25)',
                opacity: 0.99,
                p: 0.5,
                height: !isSmallScreen ? 70 : collapsed ? 70 : 'auto',
                transition: 'all 0.3s ease',
                overflow: !isSmallScreen ? 'visible' : 'hidden',
            }}
        >
            <Toolbar
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    rowGap: 1,
                    columnGap: 1,
                }}
            >
                <Button
                    onClick={() => window.location.href = "/"}
                    name="logo-button"
                    aria-label="Go to homepage"
                    sx={{ p: 0, backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}
                >
                    <img src="/logo64.webp" alt="logo" width="48" height="48" className="img-no-style" />
                    {!isSmallScreen && (
                        <img
                            src="/images/skrach_it_text.webp"
                            alt="skrach-it"
                            className="img-no-style"
                            style={{ maxWidth: '200px', marginLeft: 10 }}
                        />
                    )}
                </Button>

                {isSmallScreen && (
                    <Button
                        onClick={() => setCollapsed(!collapsed)}
                        name="menu-button"
                        aria-label="Toggle menu"
                        sx={{
                            ...menuButtonStyle,
                        }}
                    >
                        <MoreVertIcon fontSize="large" sx={{ mr: 1 }} />
                    </Button>
                )}

                {isSmallScreen && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            ml: 'auto',
                        }}
                    >
                        {sessionData.userId === 0 ? (
                            <Button
                                variant="contained"
                                component={Link}
                                to="/login"
                                name="login-button"
                                aria-label="Sign in"
                                sx={{
                                    fontSize: "1.2rem",
                                    fontWeight: "bold",
                                    backgroundColor: "#7a52e0",
                                    '&:hover': { backgroundColor: "#b4a0eb" }
                                }}
                            >
                                <AccountCircleIcon sx={{ mr: 1 }} />
                                Sign in
                            </Button>
                        ) : (
                            <Grid container alignItems="center" spacing={1} sx={{ width: 'auto', flexWrap: 'nowrap' }}>
                                {!isSmallScreen && (
                                    <Grid item>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                            {sessionData.nickname}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item>
                                    <UserMenu />
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                )}

                {/* Navigation Menu */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1.5,
                        flexGrow: 1,
                    }}
                >
                    {/* Home with Hover Menu */}
                    <Box
                        sx={{
                            position: 'relative',
                            '&:hover .dropdown-content': {
                                display: 'flex',
                            }
                        }}
                    >
                        {/* Clickable Home button */}
                        <Button
                            variant={isOnPath('/') ? 'outlined' : 'text'}
                            component={Link}
                            name="home-button"
                            aria-label="Home"
                            to="/"
                            sx={{
                                ...menuButtonStyle,
                            }}
                        >
                            <HomeRoundedIcon sx={{ mr: 1 }} />
                            {!isSmallScreen && 'Home'}
                        </Button>

                        {/* Dropdown content */}
                        <Box
                            className="dropdown-content"
                            sx={{
                                display: 'none',
                                flexDirection: 'column',
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                minWidth: 160,
                                backgroundColor: '#2d3142',
                                border: '1px solid #3e8e93',
                                borderRadius: 2,
                                zIndex: 1305,
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.4)',
                            }}
                        >
                            <Button
                                component={Link}
                                to="/"
                                name="home-menu-button"
                                aria-label="Home"
                                sx={{
                                    ...menuButtonStyle,
                                    justifyContent: 'flex-start',
                                    width: '100%',
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <HomeRoundedIcon sx={{ fontSize: 20, mr: 1 }} />
                                Home
                            </Button>
                            <Button
                                component={Link}
                                to="/welcome"
                                name="welcome-menu-button"
                                naria-label="Welcome"
                                sx={{
                                    ...menuButtonStyle,
                                    justifyContent: 'flex-start',
                                    width: '100%',
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <PsychologyIcon sx={{ fontSize: 20, mr: 1 }} />
                                Welcome
                            </Button>
                        </Box>
                    </Box>

                    <Button component={Link} to="/browse" sx={menuButtonStyle} variant={isOnPath('/browse') ? 'outlined' : 'text'} name="browse-button" aria-label="Browse Skraches">
                        <TravelExploreIcon sx={{ mr: 1 }} />
                        {!isSmallScreen && 'Browse'}
                    </Button>

                    <Button component={Link} to="/wizard" sx={menuButtonStyle} variant={isOnPath('/wizard') ? 'outlined' : 'text'} name="skrach-wizard-button" aria-label="Skrach Wizard">
                        <AutoFixHighIcon sx={{ mr: 1 }} />
                        {!isSmallScreen && 'Wizard'}
                    </Button>

                    <Box
                        sx={{
                            position: 'relative',
                            '&:hover .dropdown-content': {
                                display: 'flex',
                            }
                        }}
                    >
                        {/* Main button */}
                        <Button
                            variant={isOnPath('/streamers') || isOnPath('/live') ? 'outlined' : 'text'}
                            name="live-button"
                            aria-label="Live and Streamers"
                            component={Link}
                            to="/live"
                            sx={{
                                ...menuButtonStyle,
                                color: '#ffe066',
                            }}
                        >
                            <LiveTvIcon sx={{ mr: 1 }} />
                            {!isSmallScreen && 'Live'}
                        </Button>

                        {/* Dropdown content */}
                        <Box
                            className="dropdown-content"
                            sx={{
                                display: 'none',
                                flexDirection: 'column',
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                minWidth: 160,
                                backgroundColor: '#2d3142',
                                border: '1px solid #ffe066',
                                borderRadius: 2,
                                zIndex: 1305,
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.4)',
                            }}
                        >
                            {/*<Button*/}
                            {/*    component={Link}*/}
                            {/*    to="/live"*/}
                            {/*    name="live-menu-button"*/}
                            {/*    aria-label="Live Stream"*/}
                            {/*    sx={{*/}
                            {/*        ...menuButtonStyle,*/}
                            {/*        justifyContent: 'flex-start',*/}
                            {/*        width: '100%',*/}
                            {/*        px: 2,*/}
                            {/*        py: 1.5,*/}
                            {/*    }}*/}
                            {/*>*/}
                            {/*    <LiveTvIcon sx={{ fontSize: 20, mr: 1 }} />*/}
                            {/*    Live*/}
                            {/*</Button>*/}
                            <Button
                                component={Link}
                                to="/streamers"
                                name="streamers-menu-button"
                                naria-label="Streamers"
                                sx={{
                                    ...menuButtonStyle,
                                    justifyContent: 'flex-start',
                                    width: '100%',
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <AccountCircleIcon sx={{ fontSize: 20, mr: 1 }} />
                                Streamers
                            </Button>
                        </Box>
                    </Box>

                    <VolumeControl />

                    <ScratchSizeControl />

                    <GameModeSelectorCompact />

                    {isSmallScreen && (
                        <IconButton onClick={() => setMinimized(true)} name="close-menu-button" aria-label="Close menu">
                            <CloseIcon sx={{ color: '#fff' }} />
                        </IconButton>
                    )}
                </Box>

                {/* User/Login Section */}
                {!isSmallScreen && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            ml: 'auto',
                        }}
                    >
                        {sessionData.userId === 0 ? (
                            <Button
                                variant="contained"
                                component={Link}
                                to="/login"
                                sx={{
                                    fontSize: "1.2rem",
                                    fontWeight: "bold",
                                    backgroundColor: "#7a52e0",
                                    '&:hover': { backgroundColor: "#b4a0eb" }
                                }}
                            >
                                <AccountCircleIcon sx={{ mr: 1 }} />
                                Sign in
                            </Button>
                        ) : (
                            <Grid container alignItems="center" spacing={1} sx={{ width: 'auto', flexWrap: 'nowrap' }}>
                                {!isSmallScreen && (
                                    <Grid item>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#fff' }}>
                                            {sessionData.nickname}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item>
                                    <UserMenu />
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
}
